import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Paddle.js types
declare global {
  interface Window {
    Paddle: {
      Initialize: (options: { token: string; [key: string]: any }) => void;
      Checkout: {
        open: (options: { transactionId?: string; settings?: any }) => void;
      };
      Environment: {
        set: (env: string) => void;
      };
    };
  }
}

const CheckoutPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const paddleLoaded = useRef(false);
  const paddleInitialized = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const transactionId = searchParams.get('_ptxn');

  // Bypass ngrok warning page if present
  useEffect(() => {
    // Check if we're on ngrok warning page and auto-click "Visit Site" button
    const bypassNgrokWarning = () => {
      const pageText = document.body.innerText || document.body.textContent || '';
      
      // Check if this is ngrok warning page
      if (pageText.includes('You are about to visit') || 
          pageText.includes('ngrok.com') || 
          pageText.includes('This website is served for free through ngrok.com')) {
        
        console.log('Ngrok warning page detected');
        
        // Try multiple methods to find and click the Visit Site button
        const selectors = [
          'button',
          'a[href]',
          '[role="button"]',
          'input[type="button"]',
          'input[type="submit"]'
        ];
        
        let visitButton: HTMLElement | null = null;
        
        // Method 1: Find button by text content
        for (const selector of selectors) {
          const buttons = Array.from(document.querySelectorAll(selector));
          visitButton = buttons.find(btn => {
            const htmlBtn = btn as HTMLElement;
            const text = (btn.textContent || htmlBtn.innerText || '').toLowerCase();
            return text.includes('visit site') || text.includes('visit');
          }) as HTMLElement | null;
          
          if (visitButton) break;
        }
        
        // Method 2: Find by class or ID that might contain "visit"
        if (!visitButton) {
          const allElements = Array.from(document.querySelectorAll('*'));
          visitButton = allElements.find(el => {
            const className = (el.className || '').toLowerCase();
            const id = (el.id || '').toLowerCase();
            const text = (el.textContent || '').toLowerCase();
            return (className.includes('visit') || id.includes('visit') || text.includes('visit site')) &&
                   (el.tagName === 'BUTTON' || el.tagName === 'A' || el.getAttribute('role') === 'button');
          }) as HTMLElement | null;
        }
        
        if (visitButton) {
          console.log('Found Visit Site button, clicking...');
          // Try multiple click methods
          try {
            visitButton.click();
          } catch (e) {
            try {
              (visitButton as any).dispatchEvent(new MouseEvent('click', { bubbles: true }));
            } catch (e2) {
              console.error('Failed to click button:', e2);
            }
          }
        } else {
          console.log('Visit Site button not found, trying direct navigation...');
          // If button not found, try to navigate directly by removing query params that might cause issues
          const currentUrl = window.location.href;
          if (currentUrl.includes('ngrok-free.dev') && currentUrl.includes('checkout')) {
            // Already on correct URL, just wait
            console.log('Already on checkout URL, page should load...');
          }
        }
      }
    };

    // Check multiple times with delays
    bypassNgrokWarning();
    const timeouts = [
      setTimeout(bypassNgrokWarning, 300),
      setTimeout(bypassNgrokWarning, 800),
      setTimeout(bypassNgrokWarning, 1500)
    ];
    
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    // Load Paddle.js script
    if (!paddleLoaded.current && transactionId) {
      // Check if Paddle.js is already loaded
      if (window.Paddle) {
        openCheckout();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      script.async = true;
      script.onload = async () => {
        paddleLoaded.current = true;
        console.log('Paddle.js loaded');
        
        // Get client token from backend
        try {
          const response = await fetch('/api/v1/payment/client-token');
          if (!response.ok) {
            throw new Error(`Failed to get client token: ${response.status}`);
          }
          const data = await response.json();
          const clientToken = data.token;
          const paddleEnv = data.environment || 'sandbox';
          
          console.log('Client token received, environment:', paddleEnv);
          
          // Set environment BEFORE Initialize
          if (window.Paddle && window.Paddle.Environment) {
            window.Paddle.Environment.set(paddleEnv);
            console.log('Paddle environment set to:', paddleEnv);
          }
          
          // Initialize Paddle with client token
          if (window.Paddle && window.Paddle.Initialize) {
            window.Paddle.Initialize({
              token: clientToken,
              eventCallback: async (data: any) => {
                console.log('Paddle event:', data);
                if (data.name === 'checkout.error') {
                  console.error('Paddle checkout error:', data.data);
                  setError(language === 'tr' 
                    ? 'Ödeme formu yüklenirken bir hata oluştu. Lütfen tekrar deneyin.'
                    : 'An error occurred while loading the payment form. Please try again.');
                } else if (data.name === 'checkout.completed') {
                  // Payment completed - verify transaction and add tokens
                  console.log('Payment completed, verifying transaction...');
                  const completedTransactionId = data.data?.transactionId || transactionId;
                  
                  if (completedTransactionId) {
                    try {
                      const response = await fetch(`/api/v1/payment/verify-transaction?transaction_id=${completedTransactionId}`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                        }
                      });
                      
                      if (response.ok) {
                        const result = await response.json();
                        console.log('Transaction verified:', result);
                        
                        if (result.status === 'success') {
                          // Redirect to success page
                          window.location.href = `/payment/success?package=${new URLSearchParams(window.location.search).get('package') || ''}&transaction_id=${completedTransactionId}`;
                        } else if (result.status === 'already_processed') {
                          // Already processed, just redirect
                          window.location.href = `/payment/success?package=${new URLSearchParams(window.location.search).get('package') || ''}&transaction_id=${completedTransactionId}`;
                        } else {
                          console.warn('Transaction verification result:', result);
                          // Still redirect but show message
                          window.location.href = `/payment/success?package=${new URLSearchParams(window.location.search).get('package') || ''}&transaction_id=${completedTransactionId}&status=${result.status}`;
                        }
                      } else {
                        console.error('Failed to verify transaction:', response.status);
                        // Still redirect to success page
                        window.location.href = `/payment/success?package=${new URLSearchParams(window.location.search).get('package') || ''}&transaction_id=${completedTransactionId}`;
                      }
                    } catch (error) {
                      console.error('Error verifying transaction:', error);
                      // Still redirect to success page
                      window.location.href = `/payment/success?package=${new URLSearchParams(window.location.search).get('package') || ''}&transaction_id=${completedTransactionId}`;
                    }
                  }
                }
              }
            });
            paddleInitialized.current = true;
            console.log('Paddle.Initialize() called successfully');
          } else {
            console.error('Paddle.Initialize() not available');
            return;
          }
          
          // Wait for Paddle to be fully initialized
          const checkPaddle = setInterval(() => {
            if (window.Paddle && window.Paddle.Checkout) {
              clearInterval(checkPaddle);
              console.log('Paddle.Checkout is ready');
              // Small delay to ensure everything is initialized
              setTimeout(() => {
                openCheckout();
              }, 300);
            }
          }, 100);

          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkPaddle);
            if (!window.Paddle || !window.Paddle.Checkout) {
              console.error('Paddle.js failed to initialize');
            }
          }, 10000);
        } catch (error) {
          console.error('Failed to get client token or initialize Paddle:', error);
          const errorMessage = error instanceof Error ? error.message : String(error);
          if (errorMessage.includes('500') || errorMessage.includes('client token not configured')) {
            setError(language === 'tr' 
              ? 'Paddle client token yapılandırılmamış. Lütfen backend .env dosyasına PADDLE_CLIENT_TOKEN ekleyin.'
              : 'Paddle client token not configured. Please add PADDLE_CLIENT_TOKEN to backend .env file.');
          } else {
            setError(language === 'tr' 
              ? 'Ödeme formu yüklenirken bir hata oluştu. Lütfen tekrar deneyin.'
              : 'An error occurred while loading the payment form. Please try again.');
          }
        }
      };
      script.onerror = () => {
        console.error('Failed to load Paddle.js');
      };
      document.body.appendChild(script);

      return () => {
        // Cleanup
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }

    function openCheckout() {
      if (!transactionId || !window.Paddle || !window.Paddle.Checkout || !paddleInitialized.current) {
        console.error('Cannot open checkout: missing requirements', {
          transactionId,
          hasPaddle: !!window.Paddle,
          hasCheckout: !!(window.Paddle && window.Paddle.Checkout),
          isInitialized: paddleInitialized.current
        });
        return;
      }

      try {
        console.log('Opening Paddle checkout with transaction ID:', transactionId);
        console.log('Paddle object:', window.Paddle);
        console.log('Paddle.Checkout object:', window.Paddle.Checkout);
        
        // Open checkout with transaction ID
        window.Paddle.Checkout.open({
          transactionId: transactionId,
          settings: {
            displayMode: 'overlay',
            theme: 'light',
            locale: language === 'tr' ? 'tr' : 'en',
            allowLogout: false,
            frameTarget: 'checkout-container',
            frameInitialHeight: 450,
            frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;'
          }
        });
        
        console.log('Paddle.Checkout.open() called successfully');
      } catch (error) {
        console.error('Error opening Paddle checkout:', error);
        console.error('Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        });
        setError(language === 'tr' 
          ? 'Ödeme formu açılırken bir hata oluştu. Lütfen tekrar deneyin.'
          : 'An error occurred while opening the payment form. Please try again.');
      }
    }
  }, [transactionId, language]);

  const isTr = language === 'tr';

  if (!transactionId) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-3xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 w-full">
          <span className="eyebrow text-signal-red mb-4 block">/ {isTr ? 'GEÇERSİZ' : 'INVALID'}</span>
          <h1 className="display-h display-md mb-4">
            {isTr ? <>Bağlantı <em>geçersiz.</em></> : <>Link <em>invalid.</em></>}
          </h1>
          <p className="font-prose text-[16px] leading-relaxed text-ink-dim">
            {isTr
              ? 'Bu ödeme bağlantısı geçersiz veya süresi dolmuş. Lütfen ödeme akışını yeniden başlat.'
              : 'This payment link is invalid or expired. Please start the checkout flow again.'}
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 w-full">
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/01</span> · {isTr ? 'ÖDEME' : 'CHECKOUT'}</span>
          <span className="eyebrow-mute hidden md:inline">PADDLE · SECURE FRAME</span>
        </div>

        <div className="mb-12">
          <h1 className="display-h display-md mb-4">
            {isTr ? <>Form <em>açılıyor…</em></> : <>Loading <em>checkout…</em></>}
          </h1>
          <p className="font-prose text-[15px] text-ink-dim leading-relaxed max-w-xl">
            {isTr
              ? 'Paddle güvenli ödeme penceresini hazırlıyor. Saniyeler içinde yüklenecek.'
              : 'Paddle is preparing the secure checkout. It will load in a moment.'}
          </p>
        </div>

        {error && (
          <div className="mb-10 p-5 border border-signal-red/40 bg-signal-red/10">
            <span className="eyebrow-mute mb-1 block text-signal-red">/ ERROR</span>
            <p className="font-prose text-[14px] text-signal-red leading-relaxed">{error}</p>
          </div>
        )}

        <div className="border border-hairline">
          <div id="checkout-container" className="min-h-[450px] flex items-center justify-center p-8">
            <div id="paddle-checkout-container" className="w-full max-w-2xl">
              {!error && (
                <div className="text-center">
                  <div className="w-8 h-8 mx-auto mb-4 border border-accent border-t-transparent rounded-full animate-spin" />
                  <span className="eyebrow-mute">
                    {isTr ? 'ÖDEME FORMU HAZIRLANIYOR…' : 'PREPARING CHECKOUT…'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CheckoutPage;
